import React, { useRef, useState } from 'react';
import { FiUploadCloud, FiUpload, FiEdit2, FiCheck, FiTrash2, FiChevronDown } from 'react-icons/fi';
import SettingsModal from '../../../clientSettings/SettingsModal';
import Toggle from '../../../Toggle';
import { toast } from '../../../Toast';
import { ACCEPT, Doc, Folder, MAX_UPLOAD_BYTES, fileKind, fileSize } from './docsData';

// what the form hands back; file is set when a (new) file was chosen
export interface DocDraft {
    name: string;
    folderId: string;
    description: string;
    live: boolean;
    file?: { name: string; kind: string; size: string };
}

interface Props {
    folders: Folder[];
    // the folder it opens in (Add); Edit uses the document's own
    folderId: string;
    // left out = Add a document
    doc?: Doc;
    onClose: () => void;
    onSave: (d: DocDraft) => void;
    onDelete?: () => void;
}

// Add a document (spec 708:5288) and Edit document (spec 708:5553): same fields; Edit adds Replace File, version history and Delete
const DocumentModal: React.FC<Props> = ({ folders, folderId, doc, onClose, onSave, onDelete }) => {
    const [name, setName] = useState(doc?.name ?? '');
    const [folder, setFolder] = useState(doc?.folderId ?? folderId);
    const [description, setDescription] = useState(doc?.description ?? '');
    const [live, setLive] = useState(doc?.live ?? true);
    const [file, setFile] = useState<DocDraft['file']>();
    const fileRef = useRef<HTMLInputElement>(null);

    const pick = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0];
        e.target.value = '';
        if (!f) return;
        if (f.size > MAX_UPLOAD_BYTES) {
            toast.error('That file is over 25 MB — choose a smaller one');
            return;
        }
        setFile({ name: f.name, kind: fileKind(f.name), size: fileSize(f.size) });
        // a new upload is named after its file until you type a name
        if (!doc && !name.trim()) setName(f.name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' '));
    };

    const canSave = !!name.trim() && (!!doc || !!file);
    const save = () => onSave({ name: name.trim(), folderId: folder, description: description.trim(), live, file });
    const current = doc?.versions[0];

    let fileBox: React.ReactNode;
    if (file) {
        fileBox = <div className="hub-file-box">
            <span className="hub-file-ok"><FiCheck /></span>
            <span className="hub-file-t"><strong>{file.name}</strong><span>{file.kind} · {file.size} · {doc ? `new version — replaces v${current?.n ?? 1} when you save` : 'uploaded'}</span></span>
            <button type="button" className="hub-icon-btn danger" aria-label="Remove this file" title="Remove this file" onClick={() => setFile(undefined)}><FiTrash2 /></button>
        </div>;
    }
    else if (doc) {
        fileBox = <div className="hub-file-box">
            <span className="hub-file-ok"><FiCheck /></span>
            <span className="hub-file-t"><strong>{doc.file}</strong><span>Current version · uploaded {current?.date ?? doc.uploaded} by {doc.uploadedBy}</span></span>
            <button type="button" className="hub-file-replace" onClick={() => fileRef.current?.click()}>Replace File</button>
            <button type="button" className="hub-icon-btn danger" aria-label="Delete document" title="Delete document" onClick={onDelete}><FiTrash2 /></button>
        </div>;
    }
    else {
        fileBox = <button type="button" className="hub-file-drop" onClick={() => fileRef.current?.click()}>
            <FiUploadCloud />
            <strong>Choose a file</strong>
            <span>PDF, Word or Excel · up to 25 MB</span>
        </button>;
    }

    return <SettingsModal icon={doc ? <FiEdit2 /> : <FiUpload />} title={doc ? 'Edit Document' : 'Add a Document'}
        sub={doc ? `${doc.name} · ${doc.kind} · ${doc.size}` : 'PDF, Word or Excel · up to 25 MB'} onClose={onClose}
        footer={<>
            {doc && <button type="button" className="hub-btn-danger-outline" onClick={onDelete}><FiTrash2 /> Delete Document</button>}
            <button type="button" className="btn-primary" disabled={!canSave} onClick={save}>{doc ? 'Save Changes' : live ? 'Upload & Publish' : 'Upload'}</button>
        </>}>
        <input ref={fileRef} type="file" accept={ACCEPT} hidden onChange={pick} />
        {fileBox}

        <label className="field">
            <span className="label">Name shown on the website</span>
            <input value={name} placeholder="e.g. Spring 2026 Community Newsletter" onChange={e => setName(e.target.value)} />
        </label>
        <label className="field">
            <span className="label">Folder</span>
            <span className="hub-field-select">
                <select value={folder} onChange={e => setFolder(e.target.value)}>
                    {folders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                </select>
                <FiChevronDown />
            </span>
        </label>
        <label className="field">
            <span className="hub-soft-label">Short description (optional)</span>
            <input className="subtle" value={description} placeholder="Shown under the file name in the list" onChange={e => setDescription(e.target.value)} />
        </label>

        <div className="hub-toggle-row">
            <div className="hub-toggle-t">
                <strong>{doc ? 'Show on the website' : 'Show on the website straight away'}</strong>
                <span>{doc ? 'Turn off to hide it without deleting it' : 'Turn off to upload now and show it later'}</span>
            </div>
            <Toggle checked={live} onChange={setLive} label="Show on the website" />
        </div>

        {doc && <div className="hub-versions">
            <strong>Version history</strong>
            {doc.versions.map((v, i) => <div key={v.n} className="hub-version">
                <span>v{v.n} · {v.date}</span>
                {i === 0
                    ? <em>Current</em>
                    : <button type="button" className="link" onClick={() => toast.info('Preview only: earlier versions aren’t stored yet')}>Download</button>}
            </div>)}
            <p>Replacing the file keeps the same link, so anything already pointing at it still works.</p>
        </div>}
    </SettingsModal>;
};

export default DocumentModal;
